import { clear, el, setClass } from './dom';
import { icon, type IconName } from './Icons';

/**
 * Top toolbar (spec §19). Follows the SketchUp convention of grouped icon tools with the keyboard
 * shortcut in the tooltip, so the toolbar teaches the shortcuts rather than competing with them.
 */

export type ToolbarAction =
  | 'new'
  | 'open'
  | 'save'
  | 'save-as'
  | 'rename'
  | 'undo'
  | 'redo'
  | 'history'
  | 'select'
  | 'plant'
  | 'move'
  | 'rotate'
  | 'maturity'
  | 'paint'
  | 'sculpt'
  | 'wand'
  | 'tag'
  | 'duplicate'
  | 'link'
  | 'group'
  | 'delete'
  | 'view-perspective'
  | 'view-top'
  | 'view-presentation'
  | 'focus'
  | 'grid'
  | 'topo'
  | 'photo'
  | 'shots';

interface ToolbarButtonSpec {
  action: ToolbarAction;
  icon: IconName;
  label: string;
  shortcut?: string;
  /** Toggle buttons show a pressed state; command buttons just fire. */
  toggle?: boolean;
}

const GROUPS: { name: string; buttons: ToolbarButtonSpec[] }[] = [
  {
    name: 'Project',
    buttons: [
      { action: 'new', icon: 'new', label: 'New site' },
      { action: 'open', icon: 'open', label: 'Open' },
      { action: 'save', icon: 'save', label: 'Save', shortcut: 'Ctrl+S' },
      { action: 'save-as', icon: 'save-as', label: 'Save a copy', shortcut: 'Ctrl+Shift+S' },
      { action: 'rename', icon: 'rename', label: 'Rename site', shortcut: 'F2' },
    ],
  },
  {
    name: 'History',
    buttons: [
      { action: 'undo', icon: 'undo', label: 'Undo', shortcut: 'Ctrl+Z' },
      { action: 'redo', icon: 'redo', label: 'Redo', shortcut: 'Ctrl+Shift+Z' },
      // The long form of the two buttons beside it: the same timeline, shown whole and clickable.
      // It is a toggle rather than a command because the panel it opens is non-modal and stays.
      {
        action: 'history',
        icon: 'history',
        label: 'Operations history',
        shortcut: 'Ctrl+Shift+H',
        toggle: true,
      },
    ],
  },
  {
    name: 'Tools',
    buttons: [
      { action: 'select', icon: 'select', label: 'Select', shortcut: 'Esc', toggle: true },
      { action: 'plant', icon: 'plant', label: 'Plant', shortcut: 'P', toggle: true },
      { action: 'move', icon: 'move', label: 'Move', shortcut: 'G', toggle: true },
      { action: 'rotate', icon: 'rotate', label: 'Rotate', shortcut: 'R', toggle: true },
      { action: 'maturity', icon: 'maturity', label: 'Maturity', shortcut: 'M', toggle: true },
    ],
  },
  {
    name: 'Ground',
    buttons: [
      { action: 'paint', icon: 'paint', label: 'Paint ground', shortcut: 'B', toggle: true },
      { action: 'sculpt', icon: 'sculpt', label: 'Sculpt terrain', shortcut: 'T', toggle: true },
    ],
  },
  // Its own group, between the hand tools and the arrange commands. The wand is a tool, so it sits
  // with tools; it gets a divider of its own because it is the one control that can change several
  // kinds of thing at once, and burying it among the brushes would understate that.
  //
  // Tag sits beside it, and the pairing is the point: these two are the only tools that talk to the
  // AI rather than to the geometry. The wand asks it to change the site; the tag tells it what the
  // site means. Grouping them says that once, in the place a user looks.
  {
    name: 'Wand',
    buttons: [
      { action: 'wand', icon: 'wand', label: 'Magic wand', shortcut: 'W', toggle: true },
      { action: 'tag', icon: 'tag', label: 'Tag meaning', shortcut: 'Shift+T', toggle: true },
    ],
  },
  {
    name: 'Arrange',
    buttons: [
      { action: 'duplicate', icon: 'duplicate', label: 'Duplicate', shortcut: 'Ctrl+D' },
      { action: 'link', icon: 'link', label: 'Link as alias', shortcut: 'Ctrl+L' },
      { action: 'group', icon: 'group', label: 'Group', shortcut: 'Ctrl+G' },
      { action: 'delete', icon: 'delete', label: 'Delete', shortcut: 'Del' },
    ],
  },
  {
    name: 'View',
    buttons: [
      { action: 'view-perspective', icon: 'perspective', label: 'Perspective', shortcut: '1', toggle: true },
      { action: 'view-top', icon: 'top', label: 'Top view', shortcut: '2', toggle: true },
      { action: 'view-presentation', icon: 'presentation', label: 'Presentation', shortcut: '3', toggle: true },
      { action: 'focus', icon: 'focus', label: 'Focus selection', shortcut: 'F' },
      // Sits with the views because it is one: it changes what you can read off the site, not what
      // the site is. Its pressed state is independent of the tool group — see `update`.
      { action: 'grid', icon: 'grid', label: 'Reference grid', shortcut: 'Shift+G', toggle: true },
      // Next to the grid, because they are the same kind of thing: a lettered frame for saying
      // *where*, and a contour map for saying *how high*. Both are drawn over the garden and neither
      // is part of it. Comes up on its own with the sculpt tool — see `Application.setGroundTool`.
      { action: 'topo', icon: 'topo', label: 'Topographic map', shortcut: 'Shift+C', toggle: true },
    ],
  },
  {
    name: 'Capture',
    buttons: [
      { action: 'photo', icon: 'camera', label: 'Photo mode', shortcut: 'C', toggle: true },
      // Beside the camera because it is the other half of the same errand: one takes shots, the
      // other is where they land. It opens the full-sheet browser over the *designer* rather than
      // the drawer inside photo mode — going through the shots and composing a new one are
      // different jobs, and only one of them wants the editor hidden. See `ShotsPanel.ts`.
      { action: 'shots', icon: 'gallery', label: 'Shots', toggle: true },
    ],
  },
];

/** Every mutually exclusive tool the toolbar can show pressed. */
export type ActiveToolName =
  | 'select'
  | 'plant'
  | 'move'
  | 'rotate'
  | 'maturity'
  | 'paint'
  | 'sculpt'
  | 'wand'
  | 'tag';

export interface ToolbarState {
  activeTool: ActiveToolName;
  activeView: 'perspective' | 'top' | 'presentation';
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  hasSelection: boolean;
  multiSelection: boolean;
  /** The reference grid is an independent toggle, not one of the mutually exclusive tools. */
  gridVisible: boolean;
  /** So is the topographic map. */
  topoVisible: boolean;
  /** And so is the history panel, which holds no canvas at all. */
  historyOpen: boolean;
  /** How many operations can be undone, for the history button's tooltip. */
  historyDepth: number;
  /** And so is the shots browser, which covers the frame rather than holding it. */
  shotsOpen: boolean;
}

export class Toolbar {
  readonly root: HTMLElement;

  /** Right-hand end of the bar, holding the two slots below. */
  private readonly trailing: HTMLElement;
  /** Feedback and info — present in every build, since neither depends on a server. */
  private readonly utility: HTMLElement;
  /** The account control, empty on a deployment without accounts. */
  private readonly account: HTMLElement;
  private readonly buttons = new Map<ToolbarAction, HTMLButtonElement>();
  /** The open document's name, under the product name in the brand block. */
  private readonly documentEl: HTMLButtonElement;

  constructor(private readonly onAction: (action: ToolbarAction) => void) {
    this.root = el('header', { class: 'lg-toolbar', attrs: { role: 'toolbar' } });

    // A button rather than a label: the thing you want when you read a name you dislike is to
    // change it, and it dispatches the same `rename` action the toolbar button and F2 do.
    this.documentEl = el('button', {
      class: 'lg-toolbar__doc',
      attrs: { type: 'button', title: 'Rename this site  ·  F2', hidden: true },
      on: { click: () => this.onAction('rename') },
    });

    this.root.appendChild(
      el(
        'div',
        { class: 'lg-toolbar__brand' },
        el('span', { class: 'lg-toolbar__mark', html: icon('plant', 20) }),
        el(
          'div',
          { class: 'lg-toolbar__names' },
          el('span', { class: 'lg-toolbar__title', text: 'Lush Glade' }),
          this.documentEl,
        ),
      ),
    );

    GROUPS.forEach((group, index) => {
      if (index > 0) this.root.appendChild(el('span', { class: 'lg-toolbar__divider' }));
      const groupEl = el('div', { class: 'lg-toolbar__group', attrs: { 'aria-label': group.name } });
      for (const spec of group.buttons) {
        const button = this.createButton(spec);
        this.buttons.set(spec.action, button);
        groupEl.appendChild(button);
      }
      this.root.appendChild(groupEl);
    });

    this.root.appendChild(el('div', { class: 'lg-toolbar__spacer' }));
    // After the spacer, so these sit hard right — away from the tool groups, which is where a user
    // looks for identity, for help, and for the way to complain, in every application that has one.
    // Two slots rather than one because they are filled by different owners at different times: the
    // HUD builds the utility pair once, and the account control is rebuilt on every sign-in.
    this.utility = el('div', { class: 'lg-toolbar__utility' });
    this.account = el('div', { class: 'lg-toolbar__account' });
    this.trailing = el('div', { class: 'lg-toolbar__trailing' }, this.utility, this.account);
    this.root.appendChild(this.trailing);
  }

  /**
   * Put a control at the right-hand end of the toolbar, replacing whatever was there.
   *
   * Exists for the account menu, which only some deployments have. Replacing rather than appending
   * matters because signing in or out rebuilds that control, and appending would leave the old one
   * behind.
   */
  mountTrailing(element: HTMLElement | null): void {
    clear(this.account);
    if (element) this.account.appendChild(element);
  }

  /**
   * Put a control just inside the account slot — the feedback and info pair.
   *
   * Kept apart from {@link mountTrailing} so that mounting one can never clear the other: the
   * account control comes and goes with the session, and the help pair has to survive that.
   */
  mountUtility(element: HTMLElement | null): void {
    clear(this.utility);
    if (element) this.utility.appendChild(element);
  }

  /** Show which document is open, under the product name. An empty name hides the line. */
  setDocumentName(name: string): void {
    this.documentEl.textContent = name;
    this.documentEl.hidden = name === '';
  }

  private createButton(spec: ToolbarButtonSpec): HTMLButtonElement {
    const tooltip = spec.shortcut ? `${spec.label}  ·  ${spec.shortcut}` : spec.label;
    return el('button', {
      class: 'lg-tool',
      html: icon(spec.icon),
      title: tooltip,
      attrs: { type: 'button', 'aria-label': spec.label, 'data-action': spec.action },
      on: { click: () => this.onAction(spec.action) },
    });
  }

  /** Reflect editor state: pressed tools, current view, and which commands are available. */
  update(state: ToolbarState): void {
    for (const [action, button] of this.buttons) {
      // The view aids are not part of the exclusive tool/view set, so each is driven from its own
      // flag — otherwise the loop would clear them every time any other button became active.
      const pressed =
        action === 'grid'
          ? state.gridVisible
          : action === 'topo'
            ? state.topoVisible
            : action === 'history'
              ? state.historyOpen
              : action === 'shots'
                ? state.shotsOpen
                : action === state.activeTool || action === `view-${state.activeView}`;
      setClass(button, 'is-active', pressed);
    }
    this.setEnabled('undo', state.canUndo);
    this.setEnabled('redo', state.canRedo);
    for (const action of ['duplicate', 'delete', 'focus'] as const) {
      this.setEnabled(action, state.hasSelection);
    }
    this.setEnabled('link', state.multiSelection);
    this.setEnabled('group', state.multiSelection);

    const undo = this.buttons.get('undo');
    if (undo) undo.title = state.undoLabel ? `Undo ${state.undoLabel}  ·  Ctrl+Z` : 'Undo  ·  Ctrl+Z';
    const redo = this.buttons.get('redo');
    if (redo) redo.title = state.redoLabel ? `Redo ${state.redoLabel}  ·  Ctrl+Shift+Z` : 'Redo  ·  Ctrl+Shift+Z';
    const history = this.buttons.get('history');
    if (history) {
      const depth =
        state.historyDepth === 0
          ? 'nothing done yet'
          : `${state.historyDepth} step${state.historyDepth === 1 ? '' : 's'}`;
      history.title = `Operations history — ${depth}  ·  Ctrl+Shift+H`;
    }
  }

  private setEnabled(action: ToolbarAction, enabled: boolean): void {
    const button = this.buttons.get(action);
    if (button) button.disabled = !enabled;
  }

  dispose(): void {
    this.buttons.clear();
    this.root.remove();
  }
}
